import React, { useState, useEffect, useMemo } from 'react';
import Box from '../Box/Box';
import filterConditionalKeyValues from '../../utils/filterConditionalKeyValues';
import ActionIcons from './ActionIcons';
import Icon from '../Icon/Icon';

type TImageBase = {
  as: keyof React.JSX.IntrinsicElements;
  kind?: 'avatar' | 'background';
  alt?: string;
  title?: string;
  src?: string;
  loading?: 'lazy' | 'eager' | 'auto';
  fetchpriority?: 'high' | 'low' | 'auto';
  decoding?: 'async' | 'sync' | 'auto';
  children?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  isUploaded?: boolean;
  useUploader?: boolean;
  aspectRatio?: 'landscape' | 'portrait' | 'square';
  onDelete?: () => void;
  onRotate?: () => void;
  onHeart?: () => void;
  onUploadError?: () => void;
  onUploadSuccess?: (file: File) => void;
  onUpload?: (file: File) => Promise<any> | void;
  showActionIcons?: boolean;
};

const uploadImageText = 'Add a new photo';
const imageNotAvailableText = 'Image not available';

const ImageBase = ({
  as,
  kind,
  className = '',
  isUploaded = false,
  useUploader = false,
  onDelete = () => {},
  onRotate = () => {},
  onHeart = () => {},
  onUploadError = () => {},
  onUploadSuccess = () => {},
  onUpload = () => {},
  src,
  showActionIcons = false,
  ...rest
}: TImageBase) => {
  const [isLoading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | undefined>(src);

  const conditionalProps = useMemo(() => filterConditionalKeyValues({ modifiers: kind }), [kind]);

  useEffect(() => {
    return () => {
      if (imageUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  useEffect(() => {
    if (isUploaded) {
      setLoading(false);
    }
  }, [isUploaded]);

  const handleError = () => {
    setHasError(true);
    setLoading(false);
    onUploadError();
  };

  const handleLoad = () => {
    setLoading(false);
    setHasError(false);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setHasError(false);

    try {
      const result = onUpload(file);
      if (result instanceof Promise) {
        await result;
      }

      const objectUrl = URL.createObjectURL(file);
      setImageUrl(objectUrl);
      setLoading(false);

      // Notify the parent component about the successful upload
      onUploadSuccess(file);
    } catch (error) {
      handleError();
    }
  };

  const renderUpload = () => (
    <Box as="div" name="Image" className={`hz-Image--add-photo ${className}`}>
      <label htmlFor="file-upload" className="hz-Image--upload-label">
        <Icon name="add_image" color="text-link-default" className="add-photo-icon" />
        <p>{uploadImageText}</p>
        <input id="file-upload" type="file" style={{ display: 'none' }} onChange={handleFileChange} />
      </label>
    </Box>
  );

  const renderActionIcons = () => {
    if (!showActionIcons) {
      return null; // Do not render action icons if the flag is false
    }
    if (useUploader) {
      return (
        <Box name="image-Action-icons" as="div" className="hz-Image-icons">
          <ActionIcons icon="delete" onClick={onDelete} aria-label="Remove image" tabIndex="0" />
          <ActionIcons icon="rotate" onClick={onRotate} aria-label="Rotate clockwise" tabIndex="0" />
        </Box>
      );
    }

    if (!useUploader && !isUploaded) {
      return (
        <Box name="image-Action-icons" as="div" className="hz-Image-icons">
          <ActionIcons icon="heart" onClick={onHeart} aria-label="Favorite" tabIndex="0" />
        </Box>
      );
    }

    return null;
  };

  const renderImageContainer = () => {
    return (
      <Box as="div" name="Image-container" className="hz-Image-container">
        <Box
          {...rest}
          {...conditionalProps}
          as={as}
          name="Image"
          className={className}
          src={imageUrl}
          onError={handleError}
          onLoad={handleLoad}
          tabIndex={0}
        />
        {renderActionIcons()}
      </Box>
    );
  };

  const renderError = () => (
    <Box as="div" name="Image" className={`hz-Image--error ${className}`}>
      <Icon name="camera-off" color="text-secondary" className="error-icon" />
      <p>{imageNotAvailableText}</p>
    </Box>
  );

  const renderLoading = () => <Box as="div" name="Image" className={`hz-Image--loading ${className}`} />;

  const renderContent = () => {
    if (useUploader && !isUploaded) return renderUpload();
    if (isLoading && !hasError) return renderLoading();
    if (hasError) return renderError();
    if (imageUrl) return renderImageContainer();
    return renderImageContainer();
  };

  return renderContent();
};

export default ImageBase;
